import { Bar, BarChart, LabelList, XAxis, YAxis } from 'recharts'
import { AlertTriangle, BarChart3, type LucideIcon } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/shared/components/ui/chart'
import { shadesForBars } from '../../lib/reporteColor'
import { parseFidelizacionError } from '../../hooks/useFidelizacionError'
import { parseNumeric, ejeCategoria } from './palette'
import { formatNumero } from '../../lib/paisMapa'
import { ChartDataTableDialog } from './ChartDataTableDialog'

export interface BarDatum {
  label: string
  value: number | string
}

interface Props {
  title: string
  data: BarDatum[]
  colorNombre: string
  isLoading?: boolean
  /** Error de la query; si está presente se muestra en lugar del estado vacío. */
  error?: unknown
  icon?: LucideIcon
  formatValue?: (v: number) => string
  /** @deprecated El chart ahora llena el alto del card (con tope max-h-[70vh]). */
  maxVisible?: number
  /** Indicador compacto opcional renderizado a la derecha del título. */
  headerStat?: { value: number | string; label: string }
  /** Callback al hacer click en una barra (drill-down). Activa el cursor pointer. */
  onSelect?: (datum: BarDatum, index: number) => void
  /** Id de configuración para el formateo de números según el país del reporte. */
  configId?: number
}

const BAR_HEIGHT = 30

function StatPill({ value, label }: { value: number | string; label: string }) {
  return (
    <span className="ml-2 inline-flex items-baseline gap-1 rounded-md border border-gray-200 dark:border-gray-700 bg-muted/40 px-2 py-0.5 text-xs">
      <span className="font-semibold text-black dark:text-white">{value}</span>
      <span className="text-muted-foreground">{label}</span>
    </span>
  )
}

export function BarChartLabelLeft({
  title,
  data,
  colorNombre,
  isLoading,
  error,
  icon: Icon = BarChart3,
  formatValue,
  headerStat,
  onSelect,
  configId,
}: Props) {
  if (isLoading) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700 h-full">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">{title}</CardTitle>
            {headerStat && <StatPill value={headerStat.value} label={headerStat.label} />}
          </div>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[250px] w-full" />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    const parsed = parseFidelizacionError(error)
    return (
      <Card className="border border-gray-300 dark:border-gray-700 h-full">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">{title}</CardTitle>
            {headerStat && <StatPill value={headerStat.value} label={headerStat.label} />}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center gap-2 h-[250px] text-center px-4">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
            <p className="text-sm font-medium">{parsed.title}</p>
            <p className="text-xs text-muted-foreground">{parsed.message}</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  const sorted = [...data]
    .map((d) => ({ ...d, _num: parseNumeric(d.value) }))
    .sort((a, b) => b._num - a._num)

  if (sorted.length === 0) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700 h-full">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">{title}</CardTitle>
            {headerStat && <StatPill value={headerStat.value} label={headerStat.label} />}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[250px] text-muted-foreground text-sm">
            Sin datos disponibles
          </div>
        </CardContent>
      </Card>
    )
  }

  // Eje de categorías: en el reporte 7 los labels se muestran completos (una
  // línea) ensanchando el eje; en el resto se trunca a 22 chars con ancho fijo.
  const eje = ejeCategoria(sorted.map((d) => d.label), configId, { anchoFijo: 140, truncar: 22 })

  // Genera paleta degradada cubriendo todo el rango de items (oscuro → claro).
  const shades = shadesForBars(colorNombre)
  const chartConfig: ChartConfig = { valor: { label: 'Total' } }

  const chartData = sorted.map((item, index) => {
    const key = `cat-${index}`
    const ratio = sorted.length <= 1 ? 0 : index / (sorted.length - 1)
    const colorIdx = Math.round(ratio * (shades.length - 1))
    const color = shades[colorIdx]
    chartConfig[key] = { label: item.label, color }
    return {
      categoria: item.label,
      valor: item._num,
      displayName: eje.formatLabel(item.label),
      fill: `var(--color-${key})`,
    }
  })

  // Alto mínimo del chart = una barra por dato (BAR_HEIGHT c/u). El contenedor
  // (flex-fill) lo estira vía minHeight:100% para llenar el card cuando hay pocas
  // barras; con muchas, este alto manda y aparece scroll alineado a la barra.
  const chartHeight = chartData.length * BAR_HEIGHT

  // Solo el reporte Medifarma Perú (id 7) expone la tabla de datos completa.
  const mostrarTabla = configId === 7 && sorted.length > 0

  return (
    <Card className="border border-gray-300 dark:border-gray-700 h-full flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
          <CardTitle className="text-sm font-medium truncate">{title}</CardTitle>
          {headerStat && <StatPill value={headerStat.value} label={headerStat.label} />}
        </div>
        {mostrarTabla && (
          <ChartDataTableDialog
            title={title}
            rows={sorted}
            columns={[
              { header: 'Categoría', cell: (row) => row.label },
              {
                header: 'Total',
                align: 'right',
                cell: (row) =>
                  formatValue ? formatValue(row._num) : formatNumero(row._num, configId ?? 0),
              },
            ]}
          />
        )}
      </CardHeader>
      <CardContent className="flex-1 min-h-0">
        {/* Caja de tamaño fijo igual a la del mapa (aspect-ratio 460/340) para
            que el card quede del mismo alto que MapaRegiones en la misma fila;
            las barras que no caben se recorren con scroll interno. */}
        <div className="aspect-[460/340] w-full overflow-y-auto max-h-[70vh]">
          <ChartContainer
            config={chartConfig}
            style={{ height: `${chartHeight}px`, minHeight: '100%' }}
            className="w-full"
          >
            <BarChart
              accessibilityLayer
              data={chartData}
              layout="vertical"
              margin={{ left: 0, right: 60 }}
            >
              <YAxis
                dataKey="displayName"
                type="category"
                tickLine={false}
                tickMargin={10}
                axisLine={false}
                width={eje.width}
                tick={{ fontSize: 11 }}
                interval={0}
              />
              <XAxis dataKey="valor" type="number" hide />
              <ChartTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    formatter={(value, _name, item) => (
                      <div className="flex flex-col gap-0.5">
                        <span className="font-medium">{item.payload.categoria}</span>
                        <span className="text-muted-foreground">
                          {formatValue
                            ? formatValue(Number(value))
                            : formatNumero(Number(value), configId ?? 0)}
                        </span>
                      </div>
                    )}
                    hideLabel
                  />
                }
              />
              <Bar
                dataKey="valor"
                layout="vertical"
                radius={5}
                cursor={onSelect ? 'pointer' : 'default'}
                onClick={(entry, index) =>
                  onSelect?.({ label: entry.categoria, value: entry.valor }, index)
                }
              >
                <LabelList
                  dataKey="valor"
                  position="right"
                  className="fill-foreground"
                  fontSize={12}
                  formatter={(value: number) =>
                    formatValue ? formatValue(value) : formatNumero(value, configId ?? 0)
                  }
                />
              </Bar>
            </BarChart>
          </ChartContainer>
        </div>
      </CardContent>
    </Card>
  )
}
